import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import {
  User, Mail, Briefcase, Clock, Save, Loader2, FolderOpen, Send,
  ArrowLeft, Building2, Globe, MapPin, Tag, CheckCircle, Star,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRY_OPTIONS = [
  "FinTech / Banking", "Healthcare / MedTech", "E-Commerce / Retail",
  "SaaS / B2B Software", "Education / EdTech", "Manufacturing / Industry 4.0",
  "Logistics / Supply Chain", "Media / Entertainment", "Real Estate / PropTech",
  "Energy / KRITIS", "Public Sector / Government", "Other",
];

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", bio: "",
    // freelancer
    hourly_rate: 0, skills: "", portfolio_url: "", contractor_type: "",
    // client
    company: "", website: "", industry: "", location: "",
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/profile`, { withCredentials: true })
      .then(res => {
        setProfile(res.data);
        setForm({
          name: res.data.name || "",
          bio: res.data.bio || "",
          hourly_rate: res.data.hourly_rate || 0,
          skills: (res.data.skills || []).join(", "),
          portfolio_url: res.data.portfolio_url || "",
          contractor_type: res.data.contractor_type || "",
          company: res.data.company || "",
          website: res.data.website || "",
          industry: res.data.industry || "",
          location: res.data.location || "",
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const role = profile?.role || user?.role || "client";
  const isClient = role === "client";

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      const data = {
        name: form.name,
        bio: form.bio,
      };
      if (isClient) {
        data.company = form.company;
        data.website = form.website;
        data.industry = form.industry;
        data.location = form.location;
      } else {
        data.hourly_rate = parseFloat(form.hourly_rate) || 0;
        data.skills = form.skills.split(",").map(s => s.trim()).filter(Boolean);
        data.portfolio_url = form.portfolio_url;
        data.contractor_type = form.contractor_type;
      }
      const res = await axios.put(`${API}/profile`, data, { withCredentials: true });
      setProfile(prev => ({ ...prev, ...res.data }));
      setSaved(true);
      refreshUser();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  const accentColor = isClient ? "#00F0FF" : "#FFB800";

  return (
    <div data-testid="profile-page" className="min-h-screen pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-[#A0A0B0] hover:text-[#00F0FF] transition-colors mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Zurueck zum Dashboard
        </Link>

        <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-white mb-8">Mein Profil</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stats Sidebar */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="glass-surface rounded-lg p-6 h-fit">
            <div className="text-center mb-6">
              <div
                className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ background: `${accentColor}1A`, border: `2px solid ${accentColor}55` }}
              >
                {profile?.picture ? (
                  <img src={profile.picture} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className="w-10 h-10" style={{ color: accentColor }} />
                )}
              </div>
              <h2 className="font-['Outfit'] text-lg font-medium text-white">{profile?.name}</h2>
              <p className="text-xs text-[#A0A0B0] font-['JetBrains_Mono']">{profile?.email}</p>
              <span
                data-testid="profile-role-badge"
                className="inline-block mt-2 text-xs px-3 py-1 rounded-full font-['Outfit'] font-medium"
                style={{
                  color: accentColor,
                  background: `${accentColor}1A`,
                  border: `1px solid ${accentColor}55`,
                }}
              >
                {isClient ? "Auftraggeber" : "Auftragnehmer"}
              </span>
              {isClient && profile?.company && (
                <p className="text-xs text-[#909098] mt-2 font-['Outfit']">
                  <Building2 className="w-3 h-3 inline mr-1" /> {profile.company}
                </p>
              )}
            </div>

            <div className="space-y-4 pt-4 border-t border-white/5">
              {isClient ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#606070] flex items-center gap-2">
                      <FolderOpen className="w-3 h-3" /> Projekte erstellt
                    </span>
                    <span className="text-sm font-['JetBrains_Mono'] text-white">
                      {profile?.stats?.projects_created || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#606070] flex items-center gap-2">
                      <Briefcase className="w-3 h-3" /> Vergeben
                    </span>
                    <span className="text-sm font-['JetBrains_Mono'] text-white">
                      {profile?.stats?.projects_assigned || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#606070] flex items-center gap-2">
                      <CheckCircle className="w-3 h-3" /> Abgeschlossen
                    </span>
                    <span className="text-sm font-['JetBrains_Mono'] text-white">
                      {profile?.stats?.projects_completed || 0}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#606070] flex items-center gap-2">
                      <Send className="w-3 h-3" /> Bewerbungen
                    </span>
                    <span className="text-sm font-['JetBrains_Mono'] text-white">
                      {profile?.stats?.applications_sent || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#606070] flex items-center gap-2">
                      <Star className="w-3 h-3" /> Angenommen
                    </span>
                    <span className="text-sm font-['JetBrains_Mono'] text-white">
                      {profile?.stats?.applications_accepted || 0}
                    </span>
                  </div>
                  {profile?.hourly_rate > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#606070] flex items-center gap-2">
                        <Clock className="w-3 h-3" /> Stundensatz
                      </span>
                      <span className="text-sm font-['JetBrains_Mono'] neon-text">
                        {profile.hourly_rate} EUR
                      </span>
                    </div>
                  )}
                </>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#606070]">Mitglied seit</span>
                <span className="text-xs font-['JetBrains_Mono'] text-[#A0A0B0]">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString("de-DE") : "-"}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Edit Form */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-2">
            <form onSubmit={handleSave} className="space-y-5">
              {/* Common: name + bio */}
              <div className="glass-surface rounded-lg p-6 space-y-5">
                <h3 className="font-['Outfit'] text-sm font-medium text-[#A0A0B0] uppercase tracking-wider flex items-center gap-2">
                  <User className="w-4 h-4" style={{ color: accentColor }} /> Persoenliche Daten
                </h3>

                <div>
                  <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">Name</label>
                  <input
                    data-testid="profile-name-input"
                    type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 text-sm transition-colors outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">
                    {isClient ? "Über uns / Über dich" : "Bio / Über mich"}
                  </label>
                  <textarea
                    data-testid="profile-bio-input"
                    rows={3} value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
                    placeholder={isClient
                      ? "Was macht euer Unternehmen? Welche Projekte vergebt ihr typischerweise?"
                      : "Erzaehle etwas ueber dich und deine Erfahrung..."}
                    className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 placeholder:text-white/30 text-sm transition-colors outline-none resize-none"
                  />
                </div>
              </div>

              {/* Role-specific section */}
              {isClient ? (
                <div data-testid="client-fields" className="glass-surface rounded-lg p-6 space-y-5">
                  <h3 className="font-['Outfit'] text-sm font-medium text-[#A0A0B0] uppercase tracking-wider flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#00F0FF]" /> Auftraggeber-Profil
                  </h3>

                  <div>
                    <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">
                      Firma / Organisation
                    </label>
                    <input
                      data-testid="profile-company-input"
                      type="text" value={form.company}
                      onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
                      placeholder="z.B. Acme AG, Privat, Freier Auftraggeber"
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 placeholder:text-white/30 text-sm transition-colors outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">
                      <Tag className="w-3 h-3 inline mr-1" /> Branche
                    </label>
                    <select
                      data-testid="profile-industry-select"
                      value={form.industry}
                      onChange={e => setForm(p => ({ ...p, industry: e.target.value }))}
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 text-sm transition-colors outline-none appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-[#0F0F16]">— bitte waehlen —</option>
                      {INDUSTRY_OPTIONS.map(ind => (
                        <option key={ind} value={ind} className="bg-[#0F0F16]">{ind}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">
                      <Globe className="w-3 h-3 inline mr-1" /> Webseite
                    </label>
                    <input
                      data-testid="profile-website-input"
                      type="url" value={form.website}
                      onChange={e => setForm(p => ({ ...p, website: e.target.value }))}
                      placeholder="https://acme.com"
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 placeholder:text-white/30 text-sm transition-colors outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">
                      <MapPin className="w-3 h-3 inline mr-1" /> Standort
                    </label>
                    <input
                      data-testid="profile-location-input"
                      type="text" value={form.location}
                      onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
                      placeholder="Zürich, Schweiz"
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 placeholder:text-white/30 text-sm transition-colors outline-none"
                    />
                  </div>
                </div>
              ) : (
                <div data-testid="freelancer-fields" className="glass-surface rounded-lg p-6 space-y-5">
                  <h3 className="font-['Outfit'] text-sm font-medium text-[#A0A0B0] uppercase tracking-wider flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-[#FFB800]" /> Auftragnehmer-Details
                  </h3>

                  <div>
                    <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">Auftragnehmer-Typ</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "freelancer", l: "Freelancer" },
                        { v: "unternehmen", l: "Unternehmen" },
                        { v: "mitarbeiter_auftraggeber", l: "Mitarbeiter Auftraggeber" },
                        { v: "andere", l: "Andere" },
                      ].map(ct => (
                        <button
                          key={ct.v}
                          type="button"
                          data-testid={`profile-contractor-type-${ct.v}`}
                          onClick={() => setForm(p => ({ ...p, contractor_type: ct.v }))}
                          className={`px-3 py-2 text-xs font-['Outfit'] rounded transition-all border ${
                            form.contractor_type === ct.v
                              ? "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]"
                              : "border-white/10 bg-white/5 text-[#A0A0B0] hover:bg-white/10"
                          }`}
                        >
                          {ct.l}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">Stundensatz (EUR)</label>
                    <input
                      data-testid="profile-rate-input"
                      type="number" min="0" step="5" value={form.hourly_rate}
                      onChange={e => setForm(p => ({ ...p, hourly_rate: e.target.value }))}
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#FFB800] focus:ring-1 focus:ring-[#FFB800] px-4 py-3 font-['JetBrains_Mono'] text-sm transition-colors outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">Skills (kommagetrennt)</label>
                    <input
                      data-testid="profile-skills-input"
                      type="text" value={form.skills}
                      onChange={e => setForm(p => ({ ...p, skills: e.target.value }))}
                      placeholder="React, Python, UI/UX Design..."
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#FFB800] focus:ring-1 focus:ring-[#FFB800] px-4 py-3 placeholder:text-white/30 font-['JetBrains_Mono'] text-sm transition-colors outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">Portfolio-URL</label>
                    <input
                      data-testid="profile-portfolio-input"
                      type="url" value={form.portfolio_url}
                      onChange={e => setForm(p => ({ ...p, portfolio_url: e.target.value }))}
                      placeholder="https://mein-portfolio.de"
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#FFB800] focus:ring-1 focus:ring-[#FFB800] px-4 py-3 placeholder:text-white/30 text-sm transition-colors outline-none"
                    />
                  </div>
                </div>
              )}

              <button
                data-testid="profile-save-btn"
                type="submit" disabled={saving}
                className={`btn-primary w-full flex items-center justify-center gap-2 ${saved ? "!bg-[#00F0FF] !text-black" : ""}`}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saved ? "Gespeichert!" : "Profil speichern"}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
